'use client';

import React, { useState } from 'react';
import { Building2, User } from 'lucide-react';

type Props = {
  name: string;
  avatar: string;
  ownerType: 'individual' | 'institution';
  size?: 'sm' | 'md' | 'lg';
};

function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function hueFromName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

export default function CommunityPostAvatar({ name, avatar, ownerType, size = 'md' }: Props) {
  const [failed, setFailed] = useState(false);
  const dim = size === 'sm' ? 'h-8 w-8' : size === 'lg' ? 'h-[72px] w-[72px]' : 'h-9 w-9';
  const iconSize = size === 'sm' ? 14 : size === 'lg' ? 28 : 16;

  if (avatar && !failed) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={avatar}
        alt=""
        width={36}
        height={36}
        className={`${dim} flex-shrink-0 rounded-full object-cover ring-2 ring-white shadow-sm ${
          ownerType === 'institution' ? 'bg-violet-50 p-0.5' : ''
        }`}
        onError={() => setFailed(true)}
        loading="lazy"
        decoding="async"
      />
    );
  }

  if (ownerType === 'institution') {
    return (
      <div
        className={`flex ${dim} flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700 ring-2 ring-white shadow-sm`}
        title={name}
      >
        <Building2 size={iconSize} />
      </div>
    );
  }

  const hue = hueFromName(name);
  return (
    <div
      className={`flex ${dim} flex-shrink-0 items-center justify-center rounded-full text-[10px] font-black text-white ring-2 ring-white shadow-sm`}
      style={{ background: `hsl(${hue}, 55%, 42%)` }}
      title={name}
    >
      {initialsFromName(name) || <User size={iconSize} />}
    </div>
  );
}
